/**
 * SegmentJourneyPath — renders a segment's steps as a winding "journey map" of
 * stops instead of a flat list. Data-driven from the quest's DerivedStep[]: a
 * red trail runs through completed + current stops, a dashed trail runs ahead,
 * each stop is the milestone's authored icon (a green check badge corners it
 * once done, a red pulse marks the current one), with its name + reward beside
 * it on the outer side.
 *
 * Stops are distributed evenly inside a FIXED height so 3 or 4 of them fit the
 * same box — the anchor's central constraint ("same card size regardless of
 * step count"). A segment authored with more steps than that spacing allows
 * grows instead of letting stops collide.
 *
 * Stops are ACTIONABLE. A stop whose milestone carries an authored url (or a
 * capture subtype) renders its tile as a button routed through the shared
 * `useStepAction`, and an authored description hangs off an info affordance
 * beside the label. The map originally shipped without either, so every stop
 * was a dead label — "Link Discord Account 0/1" with no way to link Discord
 * and nothing explaining what counted. The flat card (QuestStepList) had both;
 * the journey redesign dropped them. Anchor:
 * devourplay-anchor/Play/play-the-run-card.html (runtime card) +
 * play-the-run.html (locked / missed states).
 */
import { ReactElement } from "react";
import { FaCheck, FaLock, FaExternalLinkAlt, FaArrowRight, FaUserPlus } from "react-icons/fa";
import { QuestConfigMilestone, QuestConfigMilestoneResponse } from "@devour/client";
import { DerivedStep } from "@/hooks/useOnboardingCards";
import { stepIcon } from "@/widgets/play/stepIcons";
import { useStepAction } from "@/widgets/play/useStepAction";
import "./SegmentJourneyPath.scss";

const CENTER_PCT = 50; // horizontal center of the path (percent of width)
/*
 * Winding amplitude, as a percent of width. The anchor swings ±48px inside a
 * 480px card — 10%, not 30%. At 30% a stop lands so far off-centre that its
 * caption runs past the card edge and gets clipped ("LINK DISCORD ACC…"),
 * because the caption sits OUTSIDE the tile on the outer side.
 */
const AMP_PCT = 10;
const BODY_H = 296; // fixed body height (play-the-run-card.html --bodyH)
const TOP_PX = 42; // first stop offset
const BOTTOM_PX = 42; // last stop offset

type StopState = "done" | "current" | "open" | "locked" | "missed";

/*
 * `missed` is derived here rather than carried on DerivedStep because it is a
 * function of the clock: the segment's window closes while the card is on
 * screen. Computing it in the data layer froze it at whatever `Date.now()` read
 * when the memo last ran, so a stop stayed "open" after its window had visibly
 * expired. The card owns the live countdown, so it passes the answer down.
 */
function stopState(step: DerivedStep, locked: boolean, expired: boolean): StopState {
    if (step.done) {
        return "done";
    }
    /*
     * `locked` is the whole SEGMENT being gated; `step.locked` is this one stop
     * not having opened yet (its `opensAt` is in the future). Either answer is
     * the same to a viewer — you cannot do this one — so they share a state.
     */
    if (locked || step.locked) {
        return "locked";
    }
    if (expired) {
        return "missed";
    }
    return step.current ? "current" : "open";
}

export const SegmentJourneyPath = ({ steps, milestones = [], locked = false, expired = false, onStartMilestone }: {
    steps: Array<DerivedStep>;
    /** Index-aligned with `steps` — deriveSteps maps milestones 1:1. */
    milestones?: Array<QuestConfigMilestone>;
    /** Whole segment is gated — every stop renders in the dim locked state. */
    locked?: boolean;
    /**
     * The segment's window has closed. Unfinished stops render amber with a
     * "Reopens" chip — expiry is never a dead end, the step folds back onto the
     * always-on floor (`play-the-run.html:52-56`).
     */
    expired?: boolean;
    /** Raises the shared capture modal / logs the click-URL completion event. */
    onStartMilestone?: (milestone: QuestConfigMilestoneResponse) => void;
}): ReactElement | null => {
    /* Hook must run before the early return — stable hook order across renders. */
    const resolveAction = useStepAction();
    if (!steps.length) {
        return null;
    }

    /*
     * The box NEVER grows. Stops are distributed evenly into the fixed body
     * whatever the step count — that is the anchor's central constraint ("same
     * card size regardless of step count"), and it is what keeps this slide the
     * same height as the other carousel slides. A previous version let the box
     * grow past a spacing floor, which made a 5-stop segment taller than a
     * 3-stop one and pushed the whole carousel out of alignment.
     */
    const usable = BODY_H - TOP_PX - BOTTOM_PX;
    const stepPx = steps.length > 1 ? usable / (steps.length - 1) : 0;
    const height = BODY_H;

    const points = steps.map((step, i) => ({
        xPct: CENTER_PCT + AMP_PCT * Math.sin(i * 1.15),
        yPx: TOP_PX + i * stepPx,
        state: stopState(step, locked, expired),
    }));

    // Trail splits at the last done/current stop: solid behind, dashed ahead.
    let splitIndex = 0;
    points.forEach((p, i) => {
        if (p.state === "done" || p.state === "current") {
            splitIndex = i;
        }
    });
    const traveled = points.slice(0, splitIndex + 1);
    const ahead = points.slice(splitIndex);

    /*
     * Smooth vertical cubic path through a set of (xPct, yPx) points. viewBox is
     * 0..100 in x (percent) × 0..height in y; preserveAspectRatio="none" stretches
     * to the container, and vector-effect keeps the stroke width uniform.
     */
    const pathD = (pts: Array<{ xPct: number; yPx: number }>): string => {
        if (pts.length < 2) {
            return "";
        }
        let d = `M ${pts[0].xPct} ${pts[0].yPx}`;
        for (let i = 1; i < pts.length; i++) {
            const prev = pts[i - 1];
            const cur = pts[i];
            const midY = (prev.yPx + cur.yPx) / 2;
            d += ` C ${prev.xPct} ${midY}, ${cur.xPct} ${midY}, ${cur.xPct} ${cur.yPx}`;
        }
        return d;
    };

    return (
        <div className="dp-journey-path" style={{ height }}>
            <svg
                className="dp-journey-path__trail"
                viewBox={`0 0 100 ${height}`}
                preserveAspectRatio="none"
                aria-hidden="true"
            >
                <defs>
                    <linearGradient id="dp-journey-trail" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0" stopColor="#ff3e44" />
                        <stop offset="1" stopColor="#8E0A0F" />
                    </linearGradient>
                </defs>
                {ahead.length > 1 &&
                    <path
                        d={pathD(ahead)}
                        fill="none"
                        stroke="#20202a"
                        strokeWidth={5}
                        strokeLinecap="round"
                        strokeDasharray="2 12"
                        vectorEffect="non-scaling-stroke"
                    />}
                {traveled.length > 1 &&
                    <path
                        d={pathD(traveled)}
                        fill="none"
                        stroke="url(#dp-journey-trail)"
                        strokeWidth={5}
                        strokeLinecap="round"
                        vectorEffect="non-scaling-stroke"
                    />}
            </svg>

            {steps.map((step, i) => {
                const point = points[i];
                const side = i % 2 === 0 ? "left" : "right";
                /*
                 * Caption's second line. A missed stop says why it's amber
                 * rather than showing a stale counter; otherwise the authored
                 * reward is the payoff line the anchor renders, falling back to
                 * the progress counter when a step pays nothing.
                 */
                const detail = point.state === "missed"
                    ? undefined
                    : step.done
                        ? step.reward ?? "Done"
                        : step.reward ?? step.progressText;
                /*
                 * The raw milestone drives canonical completion + the shared
                 * capture modals. QuestConfigMilestone (the config sub-document)
                 * and the wire-format QuestConfigMilestoneResponse carry the same
                 * fields — the generator just names them differently — so this
                 * cast is safe (same pattern QuestStepList uses).
                 */
                const milestone = milestones[i] as unknown as QuestConfigMilestoneResponse | undefined;
                const action = onStartMilestone
                    ? resolveAction(step, milestone, { locked, expired, onStartMilestone })
                    : null;
                /*
                 * Destination cue. A stop that leaves the page or the site
                 * looks identical to one that completes in place unless we say
                 * so — the whole reason a target icon felt like a dead label.
                 */
                const cue = action?.kind === "external"
                    ? <FaExternalLinkAlt aria-hidden />
                    : action?.kind === "navigate"
                        ? <FaArrowRight aria-hidden />
                        : action?.kind === "invite"
                            ? <FaUserPlus aria-hidden />
                            : null;
                const tileInner = (
                    <>
                        {point.state === "locked" ? <FaLock aria-hidden /> : stepIcon(step.icon)}
                        {step.done &&
                            <span className="dp-journey-path__badge">
                                <FaCheck aria-hidden />
                            </span>}
                        {cue && !step.done &&
                            <span className="dp-journey-path__cue">{cue}</span>}
                    </>
                );
                return (
                    <div
                        key={i}
                        className={`dp-journey-path__stop is-${point.state}${action ? " is-actionable" : ""}`}
                        style={{ left: `${point.xPct}%`, top: `${point.yPx}px` }}
                    >
                        {/*
                          * The tile itself is the target when the step can be
                          * acted on — it is the largest thing on the stop, so
                          * hanging a separate small button off it would be a
                          * worse hit area for the same behavior.
                          */}
                        {action
                            ? <button
                                type="button"
                                className="dp-journey-path__tile dp-journey-path__tile--action"
                                aria-label={action.label}
                                aria-describedby={step.description ? `dp-journey-tip-${i}` : undefined}
                                onClick={action.run}
                            >
                                {tileInner}
                            </button>
                            : <span className="dp-journey-path__tile">{tileInner}</span>}
                        <span className={`dp-journey-path__cap dp-journey-path__cap--${side}`}>
                            <span className="dp-journey-path__label">{step.label}</span>
                            {detail && <span className="dp-journey-path__prog">{detail}</span>}
                            {point.state === "missed" &&
                                <span className="dp-journey-path__reopens">Reopens</span>}
                            {/*
                              * WHEN it opens, not just that it is shut. A stop
                              * showing only a padlock reads as something the
                              * member did wrong; a date reads as something to
                              * come back for, which is the whole point of
                              * putting an unopened step on the ladder.
                              */}
                            {step.locked && step.opensAt &&
                                <span className="dp-journey-path__opens">
                                    Opens {new Date(step.opensAt).toLocaleDateString(undefined, {month: "short", day: "numeric"})}
                                </span>}
                        </span>
                        {/*
                          * The authored instructions ("Install the desktop app
                          * and log in."), revealed by hovering or focusing the
                          * stop — no icon to click. aria-describedby on the
                          * tile means screen readers get it without the hover.
                          */}
                        {step.description &&
                            <span className="dp-journey-path__tip" role="tooltip" id={`dp-journey-tip-${i}`}>
                                {step.description}
                            </span>}
                    </div>
                );
            })}
        </div>
    );
};

export default SegmentJourneyPath;
